import React, { useEffect, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client';
import '../site/scss/_register.scss';
import { trackClarityEvent } from './clarityAnalytics';
const axios = require('axios').default;

const Fade = ({ children }) => children;

const IOS_DEVICE_REGEX = /iPad|iPhone|iPod/;
const ANDROID_DEVICE_REGEX = /Android/i;
const ADDRESS_SELECTION_HELP_TEXT = 'Select an address from the list or add it manually below.';
const ADDRESS_DETAILS_INCOMPLETE_TEXT = 'Complete your address to continue.';
const ADDRESS_STATE_MISSING_TEXT = 'Select your state to continue.';
const ADDRESS_VERIFY_TIMEOUT_MS = 5000;
const EMAIL_CHECK_DEBOUNCE_MS = 450;
const EXISTING_EMAIL_MESSAGE = "An account already exists with this email address. Please log in to your existing account to continue. If you've forgotten your password, you can reset it from the login page.";
const BONUS_RESERVATION_SECONDS = 15 * 60;
const REGISTRATION_FUNNEL_NAME = 'signup_verification';
const REGISTRATION_FUNNEL_VERSION = '2026_08';
const REGISTRATION_FAILURE_CODES = new Set([
  'existing_account',
  'invalid_email',
  'invalid_phone',
  'ineligible_state',
  'duplicate_account',
  'validation_error',
  'rate_limited',
]);
const REGISTRATION_MANUAL_BLOCKED_STATES = ['CA', 'HI', 'ID', 'MT', 'NV', 'WA'];
const US_STATE_NAME_TO_ABBR = {
  'ALABAMA': 'AL',
  'ALASKA': 'AK',
  'ARIZONA': 'AZ',
  'ARKANSAS': 'AR',
  'CALIFORNIA': 'CA',
  'COLORADO': 'CO',
  'CONNECTICUT': 'CT',
  'DELAWARE': 'DE',
  'DISTRICT OF COLUMBIA': 'DC',
  'WASHINGTON D.C.': 'DC',
  'FLORIDA': 'FL',
  'GEORGIA': 'GA',
  'HAWAII': 'HI',
  'IDAHO': 'ID',
  'ILLINOIS': 'IL',
  'INDIANA': 'IN',
  'IOWA': 'IA',
  'KANSAS': 'KS',
  'KENTUCKY': 'KY',
  'LOUISIANA': 'LA',
  'MAINE': 'ME',
  'MARYLAND': 'MD',
  'MASSACHUSETTS': 'MA',
  'MICHIGAN': 'MI',
  'MINNESOTA': 'MN',
  'MISSISSIPPI': 'MS',
  'MISSOURI': 'MO',
  'MONTANA': 'MT',
  'NEBRASKA': 'NE',
  'NEVADA': 'NV',
  'NEW HAMPSHIRE': 'NH',
  'NEW JERSEY': 'NJ',
  'NEW MEXICO': 'NM',
  'NEW YORK': 'NY',
  'NORTH CAROLINA': 'NC',
  'NORTH DAKOTA': 'ND',
  'OHIO': 'OH',
  'OKLAHOMA': 'OK',
  'OREGON': 'OR',
  'PENNSYLVANIA': 'PA',
  'RHODE ISLAND': 'RI',
  'SOUTH CAROLINA': 'SC',
  'SOUTH DAKOTA': 'SD',
  'TENNESSEE': 'TN',
  'TEXAS': 'TX',
  'UTAH': 'UT',
  'VERMONT': 'VT',
  'VIRGINIA': 'VA',
  'WASHINGTON': 'WA',
  'WEST VIRGINIA': 'WV',
  'WISCONSIN': 'WI',
  'WYOMING': 'WY',
};
const REGISTRATION_STATE_ABBRS = new Set(Object.values(US_STATE_NAME_TO_ABBR));

const normalizeRegistrationState = (stateAbbr) => {
  const normalizedState = (stateAbbr || '').toUpperCase().trim();

  return US_STATE_NAME_TO_ABBR[normalizedState] || normalizedState;
};

const getRegistrationBlockedStates = () => {
  const cmsBlockedStates = (
    typeof window !== 'undefined'
      && window.wwRegistrationStateConfig
      && Array.isArray(window.wwRegistrationStateConfig.blockedStates)
  )
    ? window.wwRegistrationStateConfig.blockedStates
    : [];

  return [
    ...cmsBlockedStates,
    ...REGISTRATION_MANUAL_BLOCKED_STATES,
  ].map(normalizeRegistrationState);
};

const isRegistrationStateBlocked = (stateAbbr) => {
  const normalizedState = normalizeRegistrationState(stateAbbr);

  if (!normalizedState) {
    return false;
  }

  return getRegistrationBlockedStates().includes(normalizedState);
};

const normalizeFailureCategory = (code, fallback = 'request_rejected') => {
  const normalizedCode = String(code || '').trim().toLowerCase();
  return REGISTRATION_FAILURE_CODES.has(normalizedCode) ? normalizedCode : fallback;
};

const resolveNativePlatform = () => {
  if (typeof navigator === 'undefined') {
    return 'android';
  }

  const userAgent = navigator.userAgent || '';
  const platform = navigator.platform || '';
  const maxTouchPoints = navigator.maxTouchPoints || 0;

  if (IOS_DEVICE_REGEX.test(userAgent) || (platform === 'MacIntel' && maxTouchPoints > 1)) {
    return 'ios';
  }

  if (ANDROID_DEVICE_REGEX.test(userAgent)) {
    return 'android';
  }

  return 'ios';
};

const {
  formatRegistrationPhone,
  isValidRegistrationPhone,
  normalizeRegistrationPhone,
} = require('./registrationPhoneMask.js');

class RegisterForm extends React.Component {

  constructor(props) {

    const today = new Date();
    const eighteenYearsAgo = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
    const eighteenDob = eighteenYearsAgo.toISOString().split('T')[0];
    super(props);
    this.state = {
      email: '',
      emailSuggestion: null,
      emailChecking: false,
      advancingAccount: false,
      emailExists: false,
      existingEmailMessage: '',
      firstName: '',
      lastName: '',
      address_1: '',
      address_2: '',
      city: '',
      state: '',
      zip:'',
      addressMsg: '',
      existingAccountEmailHint: '',
      phone: '',
      dob: eighteenDob,
      dobStart: eighteenDob,
      dateChanged: false,
      country: 'US',
      password: '',
      tos: false,
      emailError: null,
      formMissing: '',
      formValid: false,
      formSubmitted: false,
      step: 1,
      subscibed: false,
      verifyingAddress: false,
      codeInput: '',
      codeSent: false,
      codeVerifying: false,
      codeVerified: false,
      codeError: '',
      codeStatus: '',
      showCodeField: false,
      resendCooldown: false,
      resendCooldownSeconds: 30,
      stateBlocked: false,
      stateNotifyOptIn: false,
      stateNotifyCode: '',
      stateNotifyStatus: '',
      stateNotifyVerifying: false,
      showPassword: false,
      addressFieldsExpanded: false,
      bonusReservationSeconds: BONUS_RESERVATION_SECONDS,
    }
    this.acceptEmailSuggestion = this.acceptEmailSuggestion.bind(this);
    this.onFormSubmit = this.onFormSubmit.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleFormSteps = this.handleFormSteps.bind(this);
    this.togglePasswordVisibility = this.togglePasswordVisibility.bind(this);
    this.trackedFunnelEvents = new Set();
    this.emailCheckTimer = null;
    this.emailCheckSequence = 0;
    this.checkedRegistrationEmail = '';
    this.checkedRegistrationEmailExists = false;
    this.accountAdvanceInFlight = false;
  }

  componentDidMount() {
    this.bonusReservationExpiresAt = Date.now() + (BONUS_RESERVATION_SECONDS * 1000);
    this.bonusReservationInterval = window.setInterval(() => {
      const bonusReservationSeconds = Math.max(0, Math.ceil((this.bonusReservationExpiresAt - Date.now()) / 1000));
      this.setState({ bonusReservationSeconds });

      if (bonusReservationSeconds === 0) {
        window.clearInterval(this.bonusReservationInterval);
      }
    }, 1000);

    const modalElement = document.getElementById('register-modal');
    if (!modalElement) {
      return;
    }

    modalElement.classList.add('register-native-sheet');
    modalElement.setAttribute('data-platform', resolveNativePlatform());
  }

  componentWillUnmount() {
    window.clearInterval(this.bonusReservationInterval);
    window.clearInterval(this.resendCooldownInterval);
    window.clearTimeout(this.emailCheckTimer);
    this.emailCheckSequence += 1;
  }

  togglePasswordVisibility() {
    this.setState((prevState) => ({
      showPassword: !prevState.showPassword,
    }));
  }

  handleAddressSelection = (inputValue) => {
    const normalizedState = normalizeRegistrationState(inputValue.state);
    const selectedState = REGISTRATION_STATE_ABBRS.has(normalizedState) ? normalizedState : '';
    const isBlocked = selectedState ? isRegistrationStateBlocked(selectedState) : false;
    this.updateRegistrationLeadEligibility(selectedState, false);

    this.setState({
      addressFieldsExpanded: true,
      address_1: inputValue.street,
      address_2: inputValue.street_2,
      city: inputValue.city,
      state: selectedState,
      zip: inputValue.zip,
      stateNotifyOptIn: false,
      stateNotifyCode: '',
      stateNotifyStatus: '',
      stateBlocked: isBlocked,
      existingAccountEmailHint: '',
      addressMsg: selectedState ? '' : ADDRESS_STATE_MISSING_TEXT,
      formMissing: isBlocked
        ? 'We are currently not operating in ' + selectedState + '. Check back later.'
        : (selectedState ? '' : ADDRESS_STATE_MISSING_TEXT),
      formValid: Boolean(selectedState) && !isBlocked
    }, () => this.validateForm());
  }

  async onFormSubmit(e) {
    e.preventDefault();
    const isBlocked = this.checkStateEligibility(this.state.state);
    if (isBlocked) {
      this.setState({formValid: false, formSubmitted: false});
      return;
    }

    // ensure phone is present and valid before submitting
    // Accept either 10-digit local numbers or 11-digit numbers starting with '1'
    if (!isValidRegistrationPhone(this.state.phone)) {
      this.setState({formValid: false});
      this.setState({formMissing: 'Add a valid phone number to continue (10 digits)'});
      return;
    }

    this.setState({formSubmitted: true});
    this.trackFunnelEvent('registration_submitted', { method: 'email' }, true);
    let form_data = new FormData;
    form_data.append('action', 'ww_register_user');
    const getCookie = (name) => {
      if (typeof document === 'undefined') {
        return '';
      }

      const row = document.cookie.split('; ').find((cookie) => cookie.startsWith(name + '='));
      return row ? decodeURIComponent(row.split('=').slice(1).join('=')) : '';
    };
    let metaAdsData = window.wagedWarMetaAds || '';
    if (!metaAdsData) {
      try {
        metaAdsData = localStorage.getItem('ww_meta_ads') || '';
      } catch (err) {}
    }
    if (!metaAdsData) {
      try {
        metaAdsData = sessionStorage.getItem('ww_meta_ads') || '';
      } catch (err) {}
    }
    let metaAdsLastTouch = window.wagedWarMetaAdsLastTouch || '';
    if (!metaAdsLastTouch) {
      try {
        metaAdsLastTouch = localStorage.getItem('ww_meta_ads_last_touch') || '';
      } catch (err) {}
    }
    if (!metaAdsLastTouch) {
      try {
        metaAdsLastTouch = sessionStorage.getItem('ww_meta_ads_last_touch') || '';
      } catch (err) {}
    }
    let attrSession = window.wagedWarAttrSession || '';
    if (!attrSession) {
      try {
        attrSession = localStorage.getItem('ww_attr_session') || '';
      } catch (err) {}
    }
    if (!attrSession) {
      try {
        attrSession = sessionStorage.getItem('ww_attr_session') || '';
      } catch (err) {}
    }
    attrSession = attrSession || getCookie('ww_attr_session');
    const fbp = window.wagedWarFbp || getCookie('_fbp');
    const fbc = window.wagedWarFbc || getCookie('_fbc');
		// Omnisend synchronization runs server-side after WordPress creates the user.
		const registrationData = {...this.state};
    if (metaAdsData) {
      registrationData.ww_meta_ads = metaAdsData;
      form_data.append('ww_meta_ads', metaAdsData);
    }
    if (metaAdsLastTouch) {
      registrationData.ww_meta_ads_last_touch = metaAdsLastTouch;
      form_data.append('ww_meta_ads_last_touch', metaAdsLastTouch);
    }
    if (attrSession) {
      registrationData.ww_attr_session = attrSession;
      form_data.append('ww_attr_session', attrSession);
    }
    if (fbp) {
      registrationData._fbp = fbp;
      form_data.append('_fbp', fbp);
    }
    if (fbc) {
      registrationData._fbc = fbc;
      form_data.append('_fbc', fbc);
    }
    form_data.append('data', JSON.stringify(registrationData));
    return axios.post('/wp-admin/admin-ajax.php', form_data).then(response => {
      if (response.data === 'success') {
        this.trackFunnelEvent('registration_completed', { method: 'email' }, true);
        if (document.cookie.indexOf('h2h_oid') > -1) {
          const cookie = document.cookie.split('; ').find(row => row.startsWith('h2h_oid='));
          const oid = cookie.split('=')[1];
          window.location.href = "/board/?reg=1&h2h="+oid;
        } else {
          window.location.href = "/board/?reg=1";
        }
      } else {
        const responseData = response.data && typeof response.data === 'object' ? response.data : null;
        const responseMessage = responseData?.message || response.data;
        this.trackFunnelEvent('registration_failed', {
          method: 'email',
          failure_category: normalizeFailureCategory(responseData?.code),
          failure_stage: 'account_creation',
        });
        this.setState({
          formValid: false,
          formMissing: responseMessage,
          formSubmitted: false,
          existingAccountEmailHint: responseData?.code === 'existing_account' ? (responseData.email_hint || '') : '',
        });
      }
    }).catch(() => {
      this.trackFunnelEvent('registration_failed', {
        method: 'email',
        failure_category: 'network_error',
        failure_stage: 'account_creation',
      });
      this.setState({
        formValid: false,
        formMissing: 'Network error creating your account. Please try again.',
        formSubmitted: false,
      });
    });
  }

  handleInputChange(e) {
    const { name, value } = e.target;
    const nextState = { [name]: value };
    const addressFields = ['address_1', 'address_2', 'city', 'state', 'zip', 'country'];

    if (addressFields.includes(name)) {
      nextState.addressFieldsExpanded = true;
      nextState.existingAccountEmailHint = '';
      if (this.state.existingAccountEmailHint) {
        nextState.addressMsg = '';
        nextState.formMissing = '';
      }
      if (this.state.addressMsg === ADDRESS_SELECTION_HELP_TEXT || this.state.addressMsg === ADDRESS_DETAILS_INCOMPLETE_TEXT) {
        nextState.addressMsg = '';
      }
    }

    if (['firstName', 'lastName'].includes(name)) {
      nextState.existingAccountEmailHint = '';
    }

    if (name === 'email') {
      // Any email edit invalidates the code state for the previously entered address.
      nextState.codeSent = false;
      nextState.codeVerified = false;
      nextState.codeInput = '';
      nextState.codeError = '';
      nextState.codeStatus = '';
      nextState.stateNotifyOptIn = false;
      nextState.stateNotifyCode = '';
      nextState.stateNotifyStatus = '';
      nextState.emailChecking = false;
      nextState.emailExists = false;
      nextState.existingEmailMessage = '';
    }

    this.setState(nextState);

    if (name === 'tos') {
      this.setState({tos: !this.state.tos});
    }

    if (name === 'email') {
      // Check for common domain typos and suggest a correction
      const suggestion = this.suggestEmailCorrection(value);
      let validEmail = this.isValidEmail(value);
      if (suggestion && suggestion.toLowerCase() !== value.toLowerCase()) {
        // Show only the suggestion UI (avoid duplicating the same message in emailError)
        this.setState({ emailSuggestion: suggestion, emailError: null });
      } else {
        // No suggestion or matches the typed value
        this.setState({ emailSuggestion: null });
        if (!validEmail) {
          this.setState({ emailError: 'A valid email will be required.' });
        } else {
          this.setState({ emailError: null });
        }
      }

      this.queueRegistrationEmailCheck(value);
    }

    if (name === 'state') {
      const anyBlock = isRegistrationStateBlocked(value);
      if (anyBlock) {
        this.setState({formValid: false, stateBlocked: true, stateNotifyOptIn: false, stateNotifyCode: '', stateNotifyStatus: '', formMissing: 'We are currently not operating in ' + value + '. Check back later.'});
      } else {
        this.setState({addressMsg: '', stateBlocked: false, stateNotifyOptIn: false, stateNotifyStatus: '', formMissing: ''});
      }
      this.updateRegistrationLeadEligibility(value, false);
    }

    this.validateForm();
  }

  handlePhoneChange = (value) => {
    this.setState({ phone: normalizeRegistrationPhone(value) }, () => this.validateForm());
  }

  isValidEmail(email) {
    return /\S+@\S+\.\S+/.test(email);
  }

  // Suggest corrections for common email domain typos (simple mapping)
  suggestEmailCorrection(email) {
    if (!email || typeof email !== 'string') return null;
    const parts = email.split('@');
    if (parts.length !== 2) return null;
    const local = parts[0];
    const domain = parts[1].toLowerCase();

    const commonTypos = {
      'gmal.com': 'gmail.com',
      'gmial.com': 'gmail.com',
      'gmai.com': 'gmail.com',
      'gamail.com': 'gmail.com',
      'gmail.con': 'gmail.com',
      'hotnail.com': 'hotmail.com',
      'hotmial.com': 'hotmail.com',
      'hotmai.com': 'hotmail.com',
      'yaho.com': 'yahoo.com',
      'yahho.com': 'yahoo.com',
      'outlok.com': 'outlook.com',
      'outllok.com': 'outlook.com',
      'icloud.con': 'icloud.com',
      'protonmail.con': 'protonmail.com'
    };

    // Direct mapping
    if (commonTypos[domain]) {
      return local + '@' + commonTypos[domain];
    }

    // If domain looks like common provider with small edit distance (very small heuristic)
    const providers = ['gmail.com', 'hotmail.com', 'yahoo.com', 'outlook.com', 'icloud.com', 'protonmail.com'];
    for (let p of providers) {
      // Simple heuristic: same characters but swapped or one char off and same length
      if (p.length === domain.length) {
        let diffs = 0;
        for (let i = 0; i < p.length; i++) {
          if (p[i] !== domain[i]) diffs++;
          if (diffs > 2) break;
        }
        if (diffs > 0 && diffs <= 2) {
          return local + '@' + p;
        }
      }
    }

    return null;
  }

  acceptEmailSuggestion() {
    if (this.state.emailSuggestion) {
      const suggestedEmail = this.state.emailSuggestion;
      this.setState({
        email: suggestedEmail,
        emailSuggestion: null,
        emailError: null,
        emailExists: false,
        existingEmailMessage: '',
      }, () => this.validateForm());
      this.queueRegistrationEmailCheck(suggestedEmail);
    }
  }

  queueRegistrationEmailCheck(email) {
    window.clearTimeout(this.emailCheckTimer);
    const normalizedEmail = String(email || '').trim().toLowerCase();
    const requestSequence = ++this.emailCheckSequence;
    this.checkedRegistrationEmail = '';
    this.checkedRegistrationEmailExists = false;

    if (!this.isValidEmail(normalizedEmail)) {
      return;
    }

    this.emailCheckTimer = window.setTimeout(() => {
      this.performRegistrationEmailCheck(normalizedEmail, requestSequence);
    }, EMAIL_CHECK_DEBOUNCE_MS);
  }

  async performRegistrationEmailCheck(email, requestSequence) {
    if (requestSequence !== this.emailCheckSequence) return false;

    this.setState({ emailChecking: true });
    const formData = new FormData();
    formData.append('action', 'ww_check_registration_email');
    formData.append('email', email);

    try {
      const response = await axios.post('/wp-admin/admin-ajax.php', formData);
      if (requestSequence !== this.emailCheckSequence || email !== this.state.email.trim().toLowerCase()) {
        return false;
      }

      const emailExists = Boolean(response?.data?.success && response.data.exists);
      this.checkedRegistrationEmail = email;
      this.checkedRegistrationEmailExists = emailExists;
      this.setState({
        emailChecking: false,
        emailExists,
        existingEmailMessage: emailExists ? (response.data.message || EXISTING_EMAIL_MESSAGE) : '',
      });
      return emailExists;
    } catch (error) {
      if (requestSequence === this.emailCheckSequence) {
        this.setState({ emailChecking: false });
      }
      return false;
    }
  }

  async checkRegistrationEmailNow(email) {
    const normalizedEmail = String(email || '').trim().toLowerCase();
    if (this.checkedRegistrationEmail === normalizedEmail) {
      return this.checkedRegistrationEmailExists;
    }

    window.clearTimeout(this.emailCheckTimer);
    const requestSequence = ++this.emailCheckSequence;
    return this.performRegistrationEmailCheck(normalizedEmail, requestSequence);
  }

  checkStateEligibility(stateAbbr) {
	  const isBlocked = isRegistrationStateBlocked(stateAbbr);
      if (isBlocked) {
        this.setState({ formValid: false, stateBlocked: true, addressMsg: 'We are currently not operating in ' + stateAbbr + '. Check back later.' });
      } else {
        this.setState({ addressMsg: '', stateBlocked: false, formMissing: '' });
      }
      return isBlocked;
  }

  updateRegistrationLeadEligibility = async (stateAbbr, notifyWhenAvailable = false) => {
    if (!this.state.email || !stateAbbr) return false;

    const formData = new FormData();
    formData.append('action', 'ww_registration_lead_eligibility');
    formData.append('email', this.state.email);
    formData.append('state', normalizeRegistrationState(stateAbbr));
    formData.append('notify_when_available', notifyWhenAvailable ? '1' : '0');

    try {
      await axios.post('/wp-admin/admin-ajax.php', formData);
      return true;
    } catch (error) {
      console.warn('Unable to save registration state eligibility.', error);
      return false;
    }
  }

  verifyStateAvailabilityEmail = async () => {
    if (this.state.stateNotifyCode.length !== 6 || this.state.stateNotifyVerifying) return;

    this.setState({ stateNotifyVerifying: true, stateNotifyStatus: '' });
    const formData = new FormData();
    formData.append('action', 'ww_verify_email_code');
    formData.append('email', this.state.email);
    formData.append('code', this.state.stateNotifyCode);

    try {
      const response = await axios.post('/wp-admin/admin-ajax.php', formData);
      if (!response?.data?.success) {
        this.setState({
          stateNotifyVerifying: false,
          stateNotifyStatus: response?.data?.message || 'That code could not be verified.',
        });
        return;
      }

      const saved = await this.updateRegistrationLeadEligibility(this.state.state, true);
      this.setState({
        codeVerified: true,
        stateNotifyOptIn: saved,
        stateNotifyVerifying: false,
        stateNotifyStatus: saved
          ? `You're on the list. We'll email you if Waged War opens in ${this.state.state}.`
          : 'We could not save your notification preference. Please try again.',
      });
    } catch (error) {
      this.setState({ stateNotifyVerifying: false, stateNotifyStatus: 'Unable to verify the code. Please try again.' });
    }
  }

  trackFunnelEvent(eventName, params = {}, once = false) {
    if (typeof window === 'undefined' || !eventName) return;

    const trackingKey = typeof once === 'string' ? once : eventName;
    if (once && this.trackedFunnelEvents.has(trackingKey)) return;
    if (once) this.trackedFunnelEvents.add(trackingKey);

    window.dataLayer = window.dataLayer || [];
    const eventParams = {
      funnel_name: REGISTRATION_FUNNEL_NAME,
      funnel_version: REGISTRATION_FUNNEL_VERSION,
      ...params,
    };

    window.dataLayer.push({ event: eventName, ...eventParams });
    trackClarityEvent(eventName);
  }

  validateForm() {
    setTimeout( () => {
      let msg = '';

      if (!this.state.tos) {
        msg = 'Agree to the Terms of Service to continue';
      }

      if (!this.state.password) {
        msg = 'Add a password to continue';
      }

      if (!this.state.dateChanged) {
        msg = 'Add your Date of Birth to continue';
      }

      if (this.state.dateChanged) {
        // check age
        let today = new Date();
        let age = today.getFullYear() - this.state.dob.getFullYear();
        let nineteenBlock = age < 19 && ['AL', 'NE'].includes(this.state.state);
        let twentyOneBlock = age < 21 && ['AZ', 'IA', 'LA', 'MA'].includes(this.state.state);
        if (nineteenBlock || twentyOneBlock) {
          let age = nineteenBlock ? 19 : 21;
          msg = this.state.state + ' requires you to be '+age+' to play';
        }
      }

      if (!this.state.address_1) {
        msg = 'Add your Address to continue';
      }

      if (!this.state.city) {
        msg = 'Add your City to continue';
      }

      if (!this.state.state) {
        msg = 'Add your State to continue';
      }

      if (isRegistrationStateBlocked(this.state.state)) {
        msg = 'We are currently not operating in ' + this.state.state + '. Check back later.';
      }

      if (!this.state.zip) {
        msg = 'Add your Zip to continue';
      }

      if (!this.state.country) {
        msg = 'Add your Country to continue';
      }

      if (this.state.lastName.length < 1) {
        msg = 'Add your Last name to continue';
      }

      if (this.state.firstName.length < 1) {
        msg = 'Add your First name to continue';
      }

      if (!this.state.email || !this.isValidEmail(this.state.email)) {
        msg = 'Add a valid email to continue';
      }

      // e.g. '+1 (325) 848 4888'
      if (!isValidRegistrationPhone(this.state.phone)) {
        msg = 'Add a valid phone number to continue (10 digits)';
      }

      this.setState({formMissing: msg});
      this.setState({formValid: msg.length === 0});

    }, 100);

  }

  handleLoginModal() {
    $('[data-dismiss=modal]').trigger({ type: 'click' });
    $('#login-link').trigger({ type: 'click' });
    $('[data-dismiss=modal]').trigger({ type: 'touchend' });
    $('#login-link').trigger({ type: 'touchend' });
  }

  async sendVerificationCode(successMessage = 'Code sent. Check your inbox.') {
    if (this.state.codeVerifying || !this.state.email) return;

    const attemptType = this.state.codeSent ? 'resend' : 'initial';
    this.setState({ codeVerifying: true, codeError: '', codeStatus: '' });
    const form_data = new FormData();
    form_data.append('action', 'ww_send_verification_code');
    form_data.append('email', this.state.email);

    try {
      const resp = await axios.post('/wp-admin/admin-ajax.php', form_data);
      if (resp && resp.data && resp.data.success) {
        this.trackFunnelEvent('email_verification_sent', { attempt_type: attemptType });
        this.setState({ codeSent: true, codeVerifying: false, codeStatus: successMessage });
        this.startResendCooldown();
      } else {
        const msg = (resp && resp.data && resp.data.message) ? resp.data.message : 'Could not send code. Try again.';
        const emailExists = resp?.data?.code === 'email_exists';
        this.trackFunnelEvent('email_verification_failed', {
          attempt_type: attemptType,
          failure_category: emailExists ? 'existing_account' : 'send_rejected',
          failure_stage: 'send_code',
        });
        this.setState({
          codeError: emailExists ? '' : msg,
          codeStatus: '',
          codeVerifying: false,
          step: emailExists ? 1 : this.state.step,
          emailExists,
          existingEmailMessage: emailExists ? msg : this.state.existingEmailMessage,
        });
      }
    } catch (sendErr) {
      this.trackFunnelEvent('email_verification_failed', {
        attempt_type: attemptType,
        failure_category: 'network_error',
        failure_stage: 'send_code',
      });
      this.setState({ codeError: 'Network error sending code. Try again.', codeStatus: '', codeVerifying: false });
    }
  }

  changeRegistrationEmail(event) {
    event.preventDefault();
    window.clearInterval(this.resendCooldownInterval);
    this.setState({
      step: 1,
      codeInput: '',
      codeSent: false,
      codeVerified: false,
      codeError: '',
      codeStatus: '',
      resendCooldown: false,
      resendCooldownSeconds: 30,
    });
  }

  async verifyEmailCode() {
    if (!/^\d{6}$/.test(this.state.codeInput)) {
      this.setState({ codeError: 'Enter the 6-digit code.', codeStatus: '' });
      return;
    }

    this.setState({ codeVerifying: true, codeError: '', codeStatus: '' });
    const form_data = new FormData();
    form_data.append('action','ww_verify_email_code');
    form_data.append('email', this.state.email);
    form_data.append('code', this.state.codeInput);

    try {
      const resp = await axios.post('/wp-admin/admin-ajax.php', form_data);
      if (resp && resp.data && resp.data.success) {
        this.trackFunnelEvent('email_verification_completed', { method: 'email_code' }, true);
        this.trackFunnelEvent(
          'registration_step_completed',
          { step_name: 'email_verification' },
          'registration_step_completed:email_verification'
        );
        this.setState({ codeVerified: true, codeVerifying: false, step: 2 });
      } else {
        const msg = (resp && resp.data && resp.data.message) ? resp.data.message : 'Invalid code. Try again.';
        this.trackFunnelEvent('email_verification_failed', {
          failure_category: normalizeFailureCategory(resp?.data?.code, 'invalid_or_expired_code'),
          failure_stage: 'verify_code',
        });
        this.setState({ codeError: msg, codeVerifying: false });
      }
    } catch (err) {
      this.trackFunnelEvent('email_verification_failed', {
        failure_category: 'network_error',
        failure_stage: 'verify_code',
      });
      this.setState({ codeError: 'Network error verifying code.', codeVerifying: false });
    }
  }

  async handleFormSteps(direction, event) {
    if (event && event.preventDefault) event.preventDefault();
    let isNext = direction === 'next';
    // Visual stages: Email (step 1 + verification), Location (step 2), DOB/details (step 4).
    if (isNext) {
      if (this.state.step === 1) {
        if (
          this.accountAdvanceInFlight
          || !this.state.email
          || !this.state.firstName.trim()
          || !this.state.lastName.trim()
        ) return;

        this.accountAdvanceInFlight = true;
        this.setState({ advancingAccount: true });
        const accountEmail = this.state.email.trim().toLowerCase();
        const emailExists = await this.checkRegistrationEmailNow(accountEmail);
        if (accountEmail !== this.state.email.trim().toLowerCase()) {
          this.accountAdvanceInFlight = false;
          this.setState({ advancingAccount: false });
          return;
        }
        if (emailExists) {
          this.accountAdvanceInFlight = false;
          this.setState({ advancingAccount: false });
          return;
        }
        this.trackFunnelEvent('register_started', { method: 'email' }, true);
        this.trackFunnelEvent(
          'registration_step_completed',
          { step_name: 'email_details' },
          'registration_step_completed:email_details'
        );
        this.setState({ step: 3, advancingAccount: false }, () => {
          this.accountAdvanceInFlight = false;
          if (!this.state.codeSent) {
            this.sendVerificationCode();
          }
        });
        return;
      }
      if (this.state.step === 3) {
        if (!this.state.codeVerified) return;
        this.setState({ step: 2 });
        return;
      }
    }

    if (isNext && this.state.step === 2) {
      const hasAnyAddressValue = Boolean(this.state.address_1 || this.state.address_2 || this.state.city || this.state.state || this.state.zip);
      const showAddressFields = this.state.addressFieldsExpanded || hasAnyAddressValue;
      const normalizedState = normalizeRegistrationState(this.state.state);
      const hasValidState = REGISTRATION_STATE_ABBRS.has(normalizedState);

      if (!this.state.address_1 || !this.state.city || !hasValidState || !this.state.zip) {
        this.setState({
          addressFieldsExpanded: true,
          addressMsg: !hasValidState
            ? ADDRESS_STATE_MISSING_TEXT
            : (showAddressFields ? ADDRESS_DETAILS_INCOMPLETE_TEXT : ADDRESS_SELECTION_HELP_TEXT)
        }, () => {
          const missingFieldId = !this.state.address_1
            ? 'address_1'
            : (!this.state.city ? 'city' : (!hasValidState ? 'state' : 'zip'));
          const missingField = document.getElementById(missingFieldId);
          if (missingField) {
            missingField.focus();
          }
        });
        return;
      }

      const isBlocked = this.checkStateEligibility(this.state.state);
      if (isBlocked) {
        return;
      }

      // verify address against existing customers
      const form_data = new FormData();
      form_data.append('action', 'ww_verify_registration_user');
      form_data.append('email', this.state.email);
      form_data.append('first_name', this.state.firstName);
      form_data.append('last_name', this.state.lastName);
      form_data.append('address_1', this.state.address_1);
      form_data.append('address_2', this.state.address_2);
      form_data.append('city', this.state.city);
      form_data.append('state', this.state.state);
      form_data.append('postcode', this.state.zip);
      this.setState({ verifyingAddress: true, addressMsg: '', formMissing: '' });
      let blockAdvance = false;
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => {
        try {
          controller.abort();
        } catch (abortError) {
          console.warn('Address verification abort failed', abortError);
        }
      }, ADDRESS_VERIFY_TIMEOUT_MS);

      try {
        // Do not let duplicate-account checking stall registration progression.
        const response = await axios.post('/wp-admin/admin-ajax.php', form_data, {
          signal: controller.signal,
        });
        const matches = Array.isArray(response.data) ? response.data : [];
        const bothMatch = matches.find(m => m.account_exists);
        if (bothMatch) {
          blockAdvance = true;
          this.setState({
            existingAccountEmailHint: bothMatch.email_hint || '',
            addressMsg: bothMatch.message || 'An existing account matches your details. Please log in or reset your password.',
            verifyingAddress: false,
            formMissing: ''
          });
        }
      } catch (err) {
        const wasCanceled = err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError';
        if (wasCanceled) {
          console.warn(`Address verification exceeded ${ADDRESS_VERIFY_TIMEOUT_MS}ms; allowing progression.`);
        } else {
          console.error('Address verification failed; allowing progression.', err);
        }
      } finally {
        window.clearTimeout(timeoutId);
        this.setState({ verifyingAddress: false });
      }

      if (blockAdvance) {
        return;
      }

      this.trackFunnelEvent(
        'registration_step_completed',
        { step_name: 'location' },
        'registration_step_completed:location'
      );
      this.setState({ step: 4 });
      return;
    }

    // default increment/decrement for remaining steps
    if (!isNext) {
      if (this.state.step === 2) { this.setState({ step: 3 }); return; }
      if (this.state.step === 3) { this.setState({ step: 1 }); return; }
      if (this.state.step === 4) { this.setState({ step: 2 }); return; }
    }
    let change = isNext ? this.state.step + 1 : this.state.step - 1;
    this.setState({ step: change });
  }

  startResendCooldown() {
    window.clearInterval(this.resendCooldownInterval);
    this.setState({ resendCooldown: true, resendCooldownSeconds: 30 });
    this.resendCooldownInterval = window.setInterval(() => {
      this.setState(prevState => {
        const newSeconds = prevState.resendCooldownSeconds - 1;
        if (newSeconds <= 0) {
          window.clearInterval(this.resendCooldownInterval);
          return { resendCooldown: false, resendCooldownSeconds: 30 };
        }
        return { resendCooldownSeconds: newSeconds };
      });
    }, 1000);
  }

  render() {
    const showAddressFields = this.state.addressFieldsExpanded || Boolean(this.state.address_1 || this.state.address_2 || this.state.city || this.state.state || this.state.zip);
    const normalizedAddressState = normalizeRegistrationState(this.state.state);
    const hasCompleteAddress = Boolean(
      this.state.address_1
      && this.state.city
      && REGISTRATION_STATE_ABBRS.has(normalizedAddressState)
      && this.state.zip
    );
    const bonusMinutes = Math.floor(this.state.bonusReservationSeconds / 60);
    const bonusSeconds = String(this.state.bonusReservationSeconds % 60).padStart(2, '0');
    const registrationStage = this.state.step === 4 ? 3 : (this.state.step === 2 ? 2 : 1);

    return(
      <div className="register-container">
        <form className="register-form" onSubmit={this.onFormSubmit}>
          <ol className='register-progress' aria-label='Registration progress'>
            {['Account', 'Location', 'Finish'].map((label, index) => {
              const stage = index + 1;
              const stateClass = stage === registrationStage
                ? ' is-current'
                : (stage < registrationStage ? ' is-complete' : '');

              return (
                <li key={label} className={stateClass} aria-current={stage === registrationStage ? 'step' : undefined}>
                  <span className='register-progress-number'>{stage}</span>
                  <span className='register-progress-label'>{label}</span>
                </li>
              );
            })}
          </ol>

          {this.state.step === 1 &&
            <Fade>
              <div className='register-step-one'>
                <div className="alert alert-success register-promo-active" role="alert">🎁 New Player Bonus Activated</div>
                <p className="register-promo-countdown"><strong>Bonus reserved for the next {bonusMinutes}:{bonusSeconds}</strong></p>

                <div className='register-name-grid'>
                  <label className='register-field' htmlFor="firstName">
                    <div className='reg-header'>First Name</div>
                    <input id="firstName" name="firstName" value={this.state.firstName} onChange={this.handleInputChange} autoComplete="given-name" />
                  </label>

                  <label className='register-field' htmlFor="lastName">
                    <div className='reg-header'>Last Name</div>
                    <input id="lastName" name="lastName" value={this.state.lastName} onChange={this.handleInputChange} autoComplete="family-name" />
                  </label>
                </div>

                <label className='register-field register-field--full' htmlFor="email">
                  <div className='reg-header'>Email</div>
                  <p>Will send you a verification code</p>
                  <input id="email" name="email" type='text' value={this.state.email} onChange={this.handleInputChange} autoComplete="email" />
                  {this.state.emailError && <p style={{color: 'red'}}>{this.state.emailError}</p>}
                  {this.state.emailChecking && <p role='status'>Checking email...</p>}
                  {this.state.existingEmailMessage &&
                    <div className='alert alert-danger register-existing-email-alert' role='alert'>
                      <p>{this.state.existingEmailMessage}</p>
                      <p className='register-existing-email-actions'>
                        <a className='register-existing-email-login' href='#' onClick={this.handleLoginModal}>Log in</a>
                        <a className='register-existing-email-reset' href='/?ww_forgot_password=1'>Reset password</a>
                      </p>
                    </div>
                  }
                  {this.state.emailSuggestion &&
                    <p style={{color: 'yellow', fontWeight: '500', fontSize: '14px'}}>Did you mean <a href="#" onClick={(e) => { e.preventDefault(); this.acceptEmailSuggestion(); }} style={{color: 'yellow', textDecoration: 'underline', fontWeight: '600'}}>{this.state.emailSuggestion}</a>?</p>
                  }
                </label>

                <div className='register-step-actions'>
                  <button
                    type="button"
                    disabled={
                      !this.state.email
                      || !this.state.firstName.trim()
                      || !this.state.lastName.trim()
                      || this.state.emailExists
                      || this.state.emailChecking
                      || this.state.advancingAccount
                    }
                    onClick={(e) => this.handleFormSteps('next', e)}
                    className={"form-next-btn register-account-continue" + (
                      this.state.email
                      && this.state.firstName.trim()
                      && this.state.lastName.trim()
                      && !this.state.emailExists
                      && !this.state.emailChecking
                      && !this.state.advancingAccount
                        ? ' active'
                        : ''
                    )}
                  >
                    {this.state.advancingAccount
                      ? <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Continuing...</>
                      : 'Continue'}
                  </button>
                  <p className='register-account-login'>
                    <span>Already have an account?</span>
                    <a href='#' onClick={this.handleLoginModal}>Log in</a>
                  </p>
                </div>
              </div>
            </Fade>
          }
          {this.state.step === 2 &&
            <Fade>
              <div className='register-address-step'>
                <div className='register-address-grid'>
                  <div className='register-field register-field--full'>
                    <label className='register-field-label' htmlFor="ww_address_1">Home Address</label>
                    <AddressAutoComplete
                      inputId="ww_address_1"
                      handleAddressSelection={this.handleAddressSelection.bind(this)}
                      onInputChange={() => {
                        if (!hasCompleteAddress) return;
                        this.setState({
                          address_1: '',
                          address_2: '',
                          city: '',
                          state: '',
                          zip: '',
                          stateBlocked: false,
                          addressMsg: '',
                        });
                      }}
                    />
                    {!hasCompleteAddress &&
                      <p id='register-address-guidance' className='register-address-guidance' role='status'>
                        {showAddressFields
                          ? 'Complete your street, city, state, and ZIP code to continue.'
                          : 'Select an address from the suggestions, or add it manually.'}
                      </p>}
                    {!showAddressFields &&
                      <button
                        type='button'
                        className='register-address-toggle'
                        onClick={() => this.setState({ addressFieldsExpanded: true, addressMsg: '' })}
                      >
                        Manually add Address
                      </button>}
                  </div>

                  {showAddressFields &&
                    <>
                      <label className='register-field' htmlFor="address_1">
                        <span className='register-field-label'>Street</span>
                        <input id="address_1" name="address_1" autoComplete="address-line1" value={this.state.address_1} onInput={this.handleInputChange} />
                      </label>

                      <label className='register-field' htmlFor="address_2">
                        <span className='register-field-label'>Suite/Apt</span>
                        <input id="address_2" name="address_2" autoComplete="address-line2" value={this.state.address_2} onInput={this.handleInputChange} />
                      </label>

                      <label className='register-field' htmlFor="city">
                        <span className='register-field-label'>City</span>
                        <input id="city" name="city" autoComplete="address-level2" value={this.state.city} onInput={this.handleInputChange} />
                      </label>

                      <label className='register-field' htmlFor="state">
                        <span className='register-field-label'>State</span>
                        <select id="state" name="state" autoComplete="address-level1" value={this.state.state} onChange={this.handleInputChange}>
                          <option value="">Select state</option>
                          <option value="AL">Alabama</option>
                          <option value="AK">Alaska</option>
                          <option value="AZ">Arizona</option>
                          <option value="AR">Arkansas</option>
                          <option value="CA">California</option>
                          <option value="CO">Colorado</option>
                          <option value="CT">Connecticut</option>
                          <option value="DE">Delaware</option>
                          <option value="FL">Florida</option>
                          <option value="GA">Georgia</option>
                          <option value="HI">Hawaii</option>
                          <option value="ID">Idaho</option>
                          <option value="IL">Illinois</option>
                          <option value="IN">Indiana</option>
                          <option value="IA">Iowa</option>
                          <option value="KS">Kansas</option>
                          <option value="KY">Kentucky</option>
                          <option value="LA">Louisiana</option>
                          <option value="ME">Maine</option>
                          <option value="MD">Maryland</option>
                          <option value="MA">Massachusetts</option>
                          <option value="MI">Michigan</option>
                          <option value="MN">Minnesota</option>
                          <option value="MS">Mississippi</option>
                          <option value="MO">Missouri</option>
                          <option value="MT">Montana</option>
                          <option value="NE">Nebraska</option>
                          <option value="NV">Nevada</option>
                          <option value="NH">New Hampshire</option>
                          <option value="NJ">New Jersey</option>
                          <option value="NM">New Mexico</option>
                          <option value="NY">New York</option>
                          <option value="NC">North Carolina</option>
                          <option value="ND">North Dakota</option>
                          <option value="OH">Ohio</option>
                          <option value="OK">Oklahoma</option>
                          <option value="OR">Oregon</option>
                          <option value="PA">Pennsylvania</option>
                          <option value="RI">Rhode Island</option>
                          <option value="SC">South Carolina</option>
                          <option value="SD">South Dakota</option>
                          <option value="TN">Tennessee</option>
                          <option value="TX">Texas</option>
                          <option value="UT">Utah</option>
                          <option value="VT">Vermont</option>
                          <option value="VA">Virginia</option>
                          <option value="WA">Washington</option>
                          <option value="WV">West Virginia</option>
                          <option value="WI">Wisconsin</option>
                          <option value="WY">Wyoming</option>
                        </select>
                      </label>

                      <label className='register-field' htmlFor="zip">
                        <span className='register-field-label'>Zipcode</span>
                        <input id="zip" name="zip" autoComplete="postal-code" value={this.state.zip} onInput={this.handleInputChange} />
                      </label>

                      <input type="hidden" id="country" name="country" value={this.state.country} />
                    </>}
                </div>

                {this.state.addressMsg &&
                  <div className='error-msg register-address-error'><b>{this.state.addressMsg}</b></div>}

                {this.state.existingAccountEmailHint &&
                  <p className='register-account-login register-existing-account-actions'>
                    <a href='#' onClick={this.handleLoginModal}>Log in</a>
                    <a href='/?ww_forgot_password=1'>Reset password</a>
                  </p>}

				{this.state.stateBlocked &&
				  <div className='register-state-notify' aria-labelledby='register-state-unavailable-message'>
					<p id='register-state-unavailable-message' className='register-state-unavailable' role='alert'>
					  Registration is not currently available in <strong>{this.state.state}</strong>. You can request an email alert below.
					</p>
					<label className='register-state-notify-opt-in'>
					  <input
						type='checkbox'
						checked={this.state.stateNotifyOptIn}
						onChange={(event) => {
						  const optedIn = event.target.checked;
						  this.setState({ stateNotifyOptIn: optedIn, stateNotifyStatus: '' });
						  if (!optedIn) {
							this.updateRegistrationLeadEligibility(this.state.state, false);
						  }
						}}
					  />
					  <span>Notify me by email if Waged War becomes available in {this.state.state}.</span>
					</label>

					{this.state.stateNotifyOptIn && !this.state.codeVerified &&
					  <div className='register-state-notify-verification'>
						<p>Confirm the alert with the 6-digit code we sent to {this.state.email}.</p>
						<input
						  type='text'
						  inputMode='numeric'
						  maxLength={6}
						  value={this.state.stateNotifyCode}
						  onChange={(event) => this.setState({ stateNotifyCode: event.target.value.replace(/\D/g, '') })}
						  placeholder='123456'
						  autoComplete='one-time-code'
						/>
						<button type='button' onClick={this.verifyStateAvailabilityEmail} disabled={this.state.stateNotifyVerifying || this.state.stateNotifyCode.length !== 6}>
						  {this.state.stateNotifyVerifying ? 'Verifying...' : 'Confirm Alert'}
						</button>
						<button type='button' className='register-state-notify-resend' onClick={async () => {
						  await this.sendVerificationCode('');
						  this.setState({ stateNotifyStatus: 'A new code is on the way.' });
						}}>
						  Send a new code
						</button>
					  </div>}

					{this.state.stateNotifyStatus && <p className='register-state-notify-status'>{this.state.stateNotifyStatus}</p>}
				  </div>}

                <div className='register-address-actions'>
                  <button type='button' onClick={(e) => this.handleFormSteps('prev', e)} className='form-prev-btn'>Previous</button>
                  <button
                    type='button'
                    onClick={(e) => this.handleFormSteps('next', e)}
                    className='form-next-btn'
                    aria-describedby={!hasCompleteAddress ? 'register-address-guidance' : undefined}
                    disabled={!hasCompleteAddress || this.state.stateBlocked || this.state.verifyingAddress}
                  >
                    {this.state.stateBlocked
                      ? `Unavailable in ${this.state.state}`
                      : (this.state.verifyingAddress ? <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Verifying...</> : 'Next')}
                  </button>
                </div>
              </div>
            </Fade>
          }

          {this.state.step === 3 &&
            <Fade>
              <div className='register-verification-step'>
                <div className='register-verification-icon' aria-hidden='true'>
                  <span className='register-verification-envelope'></span>
                  <span className='register-verification-check'>✓</span>
                </div>

                <div className='register-verification-heading'>
                  <h2>Check your email</h2>
                  <p>
                    {this.state.codeSent || /^\d{6}$/.test(this.state.codeInput)
                      ? 'We sent a 6-digit code to'
                      : 'Sending a 6-digit code to'}
                    <strong>{this.state.email}</strong>
                  </p>
                </div>

                <fieldset className='register-verification-code'>
                  <legend>Verification code</legend>
                  <div className='register-verification-digits'>
                    <input
                      id='verification_code'
                      className='register-verification-code-input'
                      name='verification_code'
                      type='text'
                      inputMode='numeric'
                      maxLength={6}
                      value={this.state.codeInput}
                      onChange={(event) => this.setState({ codeInput: event.target.value.replace(/\D/g, '').slice(0, 6), codeError: '' })}
                      autoComplete='one-time-code'
                      aria-label='Verification code'
                    />
                    {Array.from({ length: 6 }).map((_, index) => (
                      <span
                        key={index}
                        className={'register-verification-digit' + (this.state.codeInput[index] ? ' has-value' : '') + (index === Math.min(this.state.codeInput.length, 5) ? ' is-active' : '')}
                        aria-hidden='true'
                      >
                        {this.state.codeInput[index] || ''}
                      </span>
                    ))}
                  </div>
                </fieldset>

                {this.state.codeError && <p className='register-verification-message is-error' role='alert'>{this.state.codeError}</p>}
                {this.state.codeStatus && <p className='register-verification-message' role='status'>{this.state.codeStatus}</p>}

                <p className='register-verification-resend'>
                  <span>Didn't receive it?</span>
                  {this.state.resendCooldown ? (
                    <span className='resend-code-link is-disabled'>Resend code <span className='register-verification-timer'>(0:{String(this.state.resendCooldownSeconds).padStart(2, '0')})</span></span>
                  ) : (
                    <a
                      href='#'
                      className='resend-code-link'
                      onClick={async (event) => {
                        event.preventDefault();
                        await this.sendVerificationCode('New code sent. Check your inbox.');
                      }}
                    >
                      {this.state.codeSent ? 'Resend code' : 'Send verification code'}
                    </a>
                  )}
                </p>

                <button
                  type='button'
                  disabled={this.state.codeVerifying || !/^\d{6}$/.test(this.state.codeInput)}
                  className={'form-next-btn register-verification-submit' + (/^\d{6}$/.test(this.state.codeInput) ? ' active' : '')}
                  onClick={() => this.verifyEmailCode()}
                >
                  {this.state.codeVerifying ? (<><span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span> Verifying...</>) : 'Verify code'}
                </button>

                <p className='register-change-email'>
                  <span>Wrong email?</span>
                  <a href='#' onClick={(event) => this.changeRegistrationEmail(event)}>Change email</a>
                </p>
              </div>
            </Fade>
          }

          {this.state.step === 4 &&
            <Fade>
              <div>
              <div className='row'>
              <label className='col-12'>
                <div className='reg-header'>Date of Birth</div>
                <p className="mb-2">Let's verify you're old enough to play</p>
                <input
                  id="dob"
                  name="dob"
                  type="date"
                  min="1950-01-01"
                  max={this.state.dobStart}
                  value={this.state.dateChanged && this.state.dob instanceof Date
                    ? this.state.dob.toISOString().split('T')[0]
                    : ''}
                  onChange={(event) => {
                    const value = event.target.value;
                    if (!value) {
                      this.setState({ dob: this.state.dobStart, dateChanged: false }, () => this.validateForm());
                      return;
                    }

                    this.setState({
                      dob: new Date(`${value}T00:00:00`),
                      dateChanged: true,
                    }, () => this.validateForm());
                  }}
                />
                </label>
              </div>

              <div className='row mt-4'>
                <label className='col-12'>
                  <div className='reg-header'>Phone</div>
                  <p className="mb-2">Used for Withdrawal & Deposit Verification</p>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="+1 (999) 999-9999"
                    value={formatRegistrationPhone(this.state.phone)}
                    onChange={(event) => this.handlePhoneChange(event.target.value)}
                  />
                </label>
              </div>

              <div className='row mt-4'>
              <label htmlFor="password" className='col-12'>
                <div className='reg-header w-100'>Password</div>
                <div className="password-field">
                  <input
                    id="password"
                    name="password"
                    type={this.state.showPassword ? 'text' : 'password'}
                    value={this.state.password}
                    onChange={this.handleInputChange}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={this.togglePasswordVisibility}
                    aria-label={this.state.showPassword ? 'Hide password' : 'Show password'}
                  >
                    {this.state.showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
                </label>
              </div>

              <div className='row mt-3'>
              <label htmlFor="tos">
                <div className='reg-header mt-3'><input id="tos" name="tos" type='checkbox' checked={this.state.tos} onChange={this.handleInputChange} />Terms of Service</div>
                <p className="tos">Check here if you agree to our <a href='/terms-of-use'>Terms of use</a> and <a href='/privacy-policy'>Privacy Policy</a>. This also confirms you are of legal age to participate in contests on Waged War. I agree to communication via email, SMS and push notifications from Waged War.</p></label>
              </div>

              <div className='row'>
                <div className='col-12 mt-3 mb-2'>
                  {this.state.formValid && !this.state.formSubmitted &&
                    <button type="submit" className="form-btn-submit">LETS PLAY!</button>
                  }

                  {this.state.formSubmitted &&
                    <div>Registering... <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span></div>
                  }

                  {this.state.formMissing.length > 0 && !this.state.formSubmitted &&
                    <div className='error-msg'>{this.state.formMissing}</div>
                  }

                  {this.state.existingAccountEmailHint && !this.state.formSubmitted &&
                    <p className='register-account-login register-existing-account-actions'>
                      <a href='#' onClick={this.handleLoginModal}>Log in</a>
                      <a href='/?ww_forgot_password=1'>Reset password</a>
                    </p>}
                </div>
              </div>

              <div className='row mt-3'>
                <div className='col-6'>
                  <button type="button" onClick={(e) => this.handleFormSteps('prev', e)} className='form-prev-btn'>Previous</button>
                </div>
              </div>

            </div>
            </Fade>
          }
        </form>
      </div>
    )
  }
}


function AddressAutoComplete( {handleAddressSelection, initialValue = '', onInputChange, inputId = 'ww_address_1'} ) {
  const [inputValue, setInputValue] = useState(initialValue || '');
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const controllerRef = useRef(null);
  const dummyRef1 = useRef(null);
  const dummyRef2 = useRef(null);
  const dummyRef3 = useRef(null);
  const lastKeyPressRef = useRef(0);
  const selectionLockRef = useRef(0);
  const suppressUntilRef = useRef(0);
  const [skipAutocomplete, setSkipAutocomplete] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      callSmarty();
    }, 500);

    return () => clearTimeout(timer)
  }, [inputValue]);

  // sync initialValue from parent when it changes
  useEffect(() => {
    if (typeof initialValue === 'string' && initialValue !== inputValue) {
      setInputValue(initialValue);
    }
  }, [initialValue]);

  // abort outstanding request on unmount
  useEffect(() => {
    return () => {
      try {
        if (controllerRef.current) controllerRef.current.abort();
      } catch (e) {}
    };
  }, []);

  const callSmarty = async () => {
    try {
      // If a recent selection requested suppression, skip calls
      if (suppressUntilRef.current && Date.now() < suppressUntilRef.current) {
        setLoading(false);
        return;
      }
      if (typeof inputValue === 'object' && inputValue !== null) {
        // Update fields with selection
        handleAddressSelection(inputValue);
        return;
      }

      if (!inputValue || inputValue.length < 4) {
        setAddresses([]);
        setOpen(false);
        return;
      }

      // if we've detected browser autofill/manager, skip Smarty calls
      if (skipAutocomplete) {
        setAddresses([]);
        setOpen(false);
        setLoading(false);
        return;
      }

      setLoading(true);
      const query = inputValue ? ('' + inputValue).trim() : '';
      // cancel previous request if any
      try {
        if (controllerRef.current) {
          controllerRef.current.abort();
        }
      } catch (e) {
        // ignore
      }
      controllerRef.current = new AbortController();

      const form_data = new FormData();
      form_data.append('address', inputValue);
      form_data.append('action', 'call_smarty_autocomplete');

      let response;
      try {
        response = await axios.post('/wp-admin/admin-ajax.php', form_data, { signal: controllerRef.current.signal });
      } catch (err) {
        // If request was aborted, just clear loading and return
        if (err.name === 'CanceledError' || err.name === 'AbortError') {
          setLoading(false);
          return;
        }
        // other errors: log and continue
        console.error('Autocomplete axios error', err);
        setLoading(false);
        return;
      }

      // if the user changed the input since this request was started, ignore this response
      try {
        const currentVal = inputRef.current && inputRef.current.value ? inputRef.current.value.trim() : '';
        if (query !== currentVal) {
          setLoading(false);
          return;
        }
      } catch (e) {}

      setLoading(false);

      const data = Array.isArray(response.data) ? response.data : [];
      // If a recent selection occurred, suppress reopening suggestions
      if (selectionLockRef.current && (Date.now() - selectionLockRef.current) < 1200) {
        setAddresses([]);
        setSelectedIndex(-1);
        setOpen(false);
      } else {
        setAddresses(data);
        setSelectedIndex(-1);
        setOpen(data.length > 0);
      }

    } catch (err) {
      const spinner = document.querySelector('.spinner-border');
      if (spinner) spinner.style.display = 'none';
      console.error('Smarty autocomplete error', err);
    }
  }

  return (
    <div className="address-autocomplete-wrapper">
      <input
        id={inputId}
        ref={inputRef}
        className="address-autocomplete-input"
        name="ww_address_1"
        autoComplete="new-password"
        data-lpignore="true"
        placeholder="Start typing to Autofill Address"
        value={inputValue}
        onChange={(e) => { setInputValue(e.target.value); setSkipAutocomplete(false); if (typeof onInputChange === 'function') onInputChange(e.target.value); }}
        onFocus={() => {
          // on focus, check hidden dummy fields for autofill
          try {
            const d1 = dummyRef1.current && dummyRef1.current.value ? dummyRef1.current.value.trim() : '';
            const d2 = dummyRef2.current && dummyRef2.current.value ? dummyRef2.current.value.trim() : '';
            const d3 = dummyRef3.current && dummyRef3.current.value ? dummyRef3.current.value.trim() : '';
            if (d1 || d2 || d3) {
              setSkipAutocomplete(true);
              setOpen(false);
              return;
            }
          } catch (e) {}
          if (addresses.length) setOpen(true);
          // also detect autofill that directly populates the visible input shortly after focus
          setTimeout(() => {
            try {
              const val = inputRef.current && inputRef.current.value ? inputRef.current.value.trim() : '';
              if (val && (Date.now() - lastKeyPressRef.current) > 500) {
                setSkipAutocomplete(true);
                setOpen(false);
              }
            } catch (e) {}
          }, 120);
        }}
        onKeyDown={(e) => {
          lastKeyPressRef.current = Date.now();
          if (!open) return;
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(prev => Math.min(prev + 1, addresses.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(prev => Math.max(prev - 1, 0));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            if (selectedIndex >= 0 && addresses[selectedIndex]) {
              const sel = addresses[selectedIndex];
              try { if (controllerRef.current) controllerRef.current.abort(); } catch (e) {}
              // prevent immediate re-query after selection
              setSkipAutocomplete(true);
              // suppress any calls for a short window to avoid re-opening
              suppressUntilRef.current = Date.now() + 1500;
              selectionLockRef.current = Date.now();
              setInputValue(sel.name || sel.street || '');
              handleAddressSelection(sel);
              setAddresses([]);
              setOpen(false);
            }
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
      />

      <div className="autocomplete-spinner-wrapper">
        <div className={"spinner-border spinner-border-sm autocomplete-spinner" + (loading ? ' show' : '')} role="status" />
      </div>

      {open && addresses.length > 0 && (
        <ul role="listbox" aria-label="Address suggestions" className="address-suggestions">
          {addresses.map((a, i) => (
            <li
              key={i}
              role="option"
              aria-selected={selectedIndex === i}
              onMouseDown={(ev) => { ev.preventDefault(); }}
              onClick={() => {
                // abort any in-flight request to avoid stale responses
                try { if (controllerRef.current) controllerRef.current.abort(); } catch (e) {}
                // prevent immediate re-query after selection
                setSkipAutocomplete(true);
                // suppress any calls for a short window to avoid re-opening
                suppressUntilRef.current = Date.now() + 1500;
                // record selection time to suppress immediate reopen
                selectionLockRef.current = Date.now();
                setInputValue(a.name || a.street || '');
                handleAddressSelection(a);
                setAddresses([]);
                setOpen(false);
              }}
              onMouseEnter={() => setSelectedIndex(i)}
              className={"address-suggestion" + (selectedIndex === i ? ' selected' : '')}
            >
              <div style={{fontSize: '14px'}}>{a.name || (a.street + (a.street_2 ? ' ' + a.street_2 : ''))}</div>
              <div className="meta">{(a.city ? a.city + ', ' : '') + (a.state || '') + (a.zip ? ' ' + a.zip : '')}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

const container = document.getElementById('register-form');
if (container !== null) {
  const root = createRoot(container);
  root.render(<RegisterForm />);
}
